import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '@/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Eye, EyeOff } from 'lucide-react';
import AuthShell from '@/components/AuthShell';

export default function RegisterPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [betaClosed, setBetaClosed] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/public/config').then(({ data }) => setBetaClosed(data.closed_beta || false)).catch(() => {});
  }, []);

  const canSubmit = name.trim() && email.trim() && password.trim() && termsAccepted && !loading && !betaClosed;

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    if (password.length < 6) { setError('Пароль должен быть не короче 6 символов'); return; }
    setError('');
    setLoading(true);
    try {
      const { data } = await api.post('/auth/register', { name, email, password, marketing_consent: marketingConsent });
      if (data.requires_verification) {
        toast.success('Код подтверждения отправлен на почту');
        navigate(`/verify-email?email=${encodeURIComponent(email)}`);
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Не удалось зарегистрироваться. Попробуйте ещё раз.');
    }
    setLoading(false);
  };

  return (
    <AuthShell>
      <div data-testid="register-page">
        <h1 className="font-heading text-xl mb-1" style={{ color: 'var(--app-text)' }}>Создать аккаунт</h1>
        <p className="text-sm mb-6" style={{ color: 'var(--app-text-muted)' }}>Начните вести практику за 30 секунд</p>

        <form onSubmit={handleRegister} className="space-y-4" noValidate>
          {betaClosed && (
            <div className="p-3 rounded-lg text-center text-sm" style={{ background: 'var(--app-accent-muted)', color: 'var(--app-accent)' }}>
              Закрытое бета-тестирование. Регистрация по приглашению.
            </div>
          )}
          {error && (
            <div role="alert" className="px-3 py-2.5 rounded-lg text-sm" style={{ background: 'var(--app-accent-muted)', color: 'var(--app-accent)' }}>
              {error}
            </div>
          )}
          <div>
            <label htmlFor="register-name" className="text-stone-400 text-sm font-medium mb-2 block">Ваше имя</label>
            <Input id="register-name" name="name" data-testid="register-name" autoComplete="name"
              value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Как к вам обращаться" className="bg-stone-950/50 border-stone-800 focus:border-amber-700 text-stone-200 placeholder:text-stone-600 h-11" autoFocus />
          </div>
          <div>
            <label htmlFor="register-email" className="text-stone-400 text-sm font-medium mb-2 block">Email</label>
            <Input id="register-email" name="email" data-testid="register-email" type="email" autoComplete="email"
              value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="email@example.com" className="bg-stone-950/50 border-stone-800 focus:border-amber-700 text-stone-200 placeholder:text-stone-600 h-11" />
          </div>
          <div>
            <label htmlFor="register-password" className="text-stone-400 text-sm font-medium mb-2 block">Пароль</label>
            <div className="relative">
              <Input id="register-password" name="password" data-testid="register-password" type={showPassword ? 'text' : 'password'} autoComplete="new-password"
                value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="Минимум 6 символов" className="bg-stone-950/50 border-stone-800 focus:border-amber-700 text-stone-200 placeholder:text-stone-600 h-11 pr-11" />
              <button type="button" onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1.5 rounded-md hover:bg-white/5 transition-colors"
                style={{ color: 'var(--app-text-muted)' }}>
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {/* Terms checkbox (required) */}
          <label className="flex items-start gap-3 cursor-pointer group">
            <input type="checkbox" checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)}
              className="mt-1 w-4 h-4 rounded border-stone-700 shrink-0" style={{ accentColor: 'var(--app-accent)' }} />
            <span className="text-xs leading-relaxed text-stone-400 group-hover:text-stone-300 transition-colors">
              Я принимаю условия{' '}
              <a href="/offer" target="_blank" rel="noopener noreferrer" className="text-amber-500 hover:text-amber-400 underline underline-offset-2">Публичной оферты</a>
              {' '}и даю согласие на обработку моих персональных данных в соответствии с{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-amber-500 hover:text-amber-400 underline underline-offset-2">Политикой конфиденциальности</a>.
            </span>
          </label>

          {/* Marketing checkbox (optional) */}
          <label className="flex items-start gap-3 cursor-pointer group">
            <input type="checkbox" checked={marketingConsent} onChange={(e) => setMarketingConsent(e.target.checked)}
              className="mt-1 w-4 h-4 rounded border-stone-700 shrink-0" style={{ accentColor: 'var(--app-accent)' }} />
            <span className="text-xs leading-relaxed text-stone-500 group-hover:text-stone-400 transition-colors">
              Я хочу получать персональные спецпредложения, анонсы новых функций и полезные материалы для развития частной практики.
            </span>
          </label>

          <Button data-testid="register-submit-btn" type="submit" disabled={!canSubmit}
            className={`w-full h-11 font-medium transition-all active:scale-[0.98] ${canSubmit ? 'bg-amber-700 hover:bg-amber-600 text-white' : 'bg-stone-800 text-stone-600 cursor-not-allowed'}`}>
            {loading ? 'Регистрация...' : 'Зарегистрироваться'}
          </Button>
        </form>

        <p className="text-stone-500 text-sm text-center mt-6">
          Уже есть аккаунт?{' '}
          <Link to="/login" className="text-amber-500 hover:text-amber-400 transition-colors">Войти</Link>
        </p>
      </div>
    </AuthShell>
  );
}
